import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export interface CreditStatus {
  user_tier: string;
  is_unlimited: boolean;
  credits_remaining: number;
  monthly_credits_limit: number;
  credits_used: number;
  next_reset: string | null;
  needs_upgrade: boolean;
  upgrade_reason: string | null;
  can_purchase_credits: boolean;
  subscription_status: string;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  price_ils: number;
  credits: number;
  features: string[];
  is_current: boolean;
  is_featured: boolean;
}


export const useCredits = () => {
  const [creditStatus, setCreditStatus] = useState<CreditStatus | null>(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t, i18n } = useTranslation();

  const getApiBaseUrl = () => {
    if (process.env.NODE_ENV === 'production') {
      return window.location.origin;
    }
    return process.env.REACT_APP_API_URL || 'http://localhost:8000';
  };

  const fetchCreditStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token');
        return;
      }

      const response = await fetch(`${getApiBaseUrl()}/api/stripe/user-payment-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch credit status');
      }

      const data = await response.json();
      setCreditStatus(data);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching credit status:', err);
    }
  }, []);

  const fetchPlansAndPackages = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No authentication token for fetching plans');
        return;
      }

      const language = i18n.language || 'he';
      
      const plansResponse = await fetch(`${getApiBaseUrl()}/api/stripe/subscription-plans?language=${language}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (plansResponse.ok) {
        const plansData = await plansResponse.json();
        setSubscriptionPlans(plansData.plans || []);
      }
    } catch (err: any) {
      console.error('Error fetching plans:', err);
    }
  }, [i18n.language]);

  const createSubscriptionCheckout = async (planId: string): Promise<string> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${getApiBaseUrl()}/api/stripe/create-subscription-checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ plan_id: planId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      return data.url;
    } catch (err: any) {
      throw new Error(err.message);
    }
  };


  const refreshCredits = useCallback(async () => {
    await fetchCreditStatus();
  }, [fetchCreditStatus]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchCreditStatus(),
        fetchPlansAndPackages()
      ]);
      setLoading(false);
    };

    loadData();
  }, [fetchCreditStatus, fetchPlansAndPackages]);

  // Helper functions
  const getCreditColor = (remaining: number, limit: number) => {
    const percentage = remaining / limit;
    if (percentage <= 0.1) return 'text-red-500';
    if (percentage <= 0.3) return 'text-orange-500';
    return 'text-green-500';
  };

  const getCreditStatusText = (status: CreditStatus) => {
    if (status.is_unlimited) {
      return t('credits:status.unlimited');
    }
    
    if (status.credits_remaining === 0) {
      return t('credits:status.no_credits');
    }
    
    if (status.credits_remaining <= 1) {
      return t('credits:status.low_credits');
    }
    
    return t('credits:status.credits_available', { count: status.credits_remaining });
  };

  const formatNextReset = (dateString: string | null) => {
    if (!dateString) return null;
    
    const resetDate = new Date(dateString);
    const now = new Date();
    const diffTime = resetDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) {
      return t('credits:reset.today');
    } else if (diffDays === 1) {
      return t('credits:reset.tomorrow');
    } else {
      return t('credits:reset.in_days', { days: diffDays });
    }
  };

  return {
    creditStatus,
    subscriptionPlans,
    loading,
    error,
    refreshCredits,
    createSubscriptionCheckout,
    getCreditColor,
    getCreditStatusText,
    formatNextReset
  };
};